// @ts-check
// Configuração de um passo no painel lateral (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): o
// nome do passo (opcional) no topo, o formulário do tipo e, no rodapé, "Excluir passo" e "Fechar". As mudanças valem
// no rascunho na hora (sem "aplicar"), como no RD; "Salvar" grava o fluxo.
import { html } from 'htm/preact';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { MAXIMUM_FLOW_NODE_NAME_LENGTH } from '../../../../shared/flows/flow-limits.js';
import { FlowSidePanel } from './flow-side-panel.js';
import { FlowCategoryCircle, FlowField, describeNode, flowProblemText } from './flow-ui.js';
import { EmailNodeForm } from './node-forms/email-node-form.js';
import { WhatsappNodeForm } from './node-forms/whatsapp-node-form.js';
import { WaitForm, WaitUntilDateForm, WaitWindowForm } from './node-forms/wait-forms.js';
import { ConditionForm, EngagementForm } from './node-forms/split-forms.js';
import { AddToFlowForm, AssignOwnerForm, RemoveFromFlowsForm, TagsForm, UpdateStageForm } from './node-forms/lead-forms.js';
import { AutomationActionForm } from './node-forms/automation-action-form.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} FlowSummaryLookups
 * @typedef {import('./flow-lookups.js').FlowLookupData & { reload: () => void }} FlowLookupState
 * @typedef {{ node: FlowNode, definition: FlowDefinition, flowId: string, problems: FlowProblem[], lookupData: FlowLookupState,
 *   lookups: FlowSummaryLookups, settings: FlowDefinition['settings'], onConfig: (patch: Record<string, unknown>) => void,
 *   onReplaceConfig: (config: Record<string, unknown>) => void }} NodeFormProps
 */

const FORMS = /** @type {Record<string, (props: NodeFormProps) => any>} */ ({
  SEND_EMAIL: EmailNodeForm,
  SEND_WHATSAPP: WhatsappNodeForm,
  WAIT: WaitForm,
  WAIT_WINDOW: WaitWindowForm,
  WAIT_UNTIL_DATE: WaitUntilDateForm,
  CONDITION: ConditionForm,
  EMAIL_ENGAGEMENT: EngagementForm,
  ADD_TO_FLOW: AddToFlowForm,
  REMOVE_FROM_FLOWS: RemoveFromFlowsForm,
  UPDATE_STAGE: UpdateStageForm,
  ADD_TAGS: TagsForm,
  REMOVE_TAGS: TagsForm,
  ASSIGN_OWNER: AssignOwnerForm,
  UPDATE_FIELD: AutomationActionForm,
  CREATE_TASK: AutomationActionForm,
  SEND_NOTIFICATION: AutomationActionForm,
  CALL_WEBHOOK: AutomationActionForm,
  HTTP_REQUEST: AutomationActionForm,
});

/**
 * @param {Omit<NodeFormProps, 'onConfig' | 'onReplaceConfig'> & { onChange: (patch: { name?: string | null, config?: Record<string, unknown>,
 *   replaceConfig?: Record<string, unknown> }) => void, onDelete: () => void, onClose: () => void }} props
 */
export function FlowNodePanel({ node, definition, flowId, problems, lookupData, lookups, settings, onChange, onDelete, onClose }) {
  const Form = FORMS[node.type];
  const described = describeNode(node, lookups);
  const formProps = /** @type {NodeFormProps} */ ({
    node, definition, flowId, problems, lookupData, lookups, settings,
    onConfig: (patch) => onChange({ config: patch }),
    onReplaceConfig: (config) => onChange({ replaceConfig: config }),
  });
  const otherProblems = problems.filter((problem) => !problem.details?.field && !['NODE_INCOMPLETE'].includes(problem.code));
  return html`<${FlowSidePanel} title=${described.summary.name ?? described.title} help=${t(`flows.panel.help.${node.type}`)} onClose=${onClose}
    panelKey=${node.id} testId="flow-node-panel"
    headerExtra=${html`<${FlowCategoryCircle} type=${node.type} config=${node.config} size=${24} />`}
    footer=${html`<button type="button" class="flow-danger-link" onClick=${onDelete}><${Icon} name="IconTrash" size=${14} /><span>${t('flows.panel.deleteNode')}</span></button>
      <${Button} title=${t('flows.panel.close')} variant="secondary" size="small" onClick=${onClose} />`}>
    <${FlowField} label=${t('flows.panel.nodeName')} htmlFor=${`flow-node-name-${node.id}`} hint=${t('flows.panel.nodeNameHint')}>
      <input id=${`flow-node-name-${node.id}`} class="text-input" value=${node.name ?? ''} maxLength=${MAXIMUM_FLOW_NODE_NAME_LENGTH}
        placeholder=${t(`flows.nodes.${node.type}.name`)}
        onInput=${(/** @type {InputEvent} */ event) => onChange({ name: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
    <//>
    ${otherProblems.length > 0 && !Form
      ? otherProblems.map((problem) => html`<p class="flow-field-error" role="alert">${flowProblemText(problem)}</p>`)
      : null}
    ${Form ? html`<${Form} key=${node.id} ...${formProps} />` : html`<p class="flow-field-hint">${t('flows.panel.unknownType')}</p>`}
  <//>`;
}
